import { useEffect, useRef, type ReactNode } from 'react';

interface Props {
  /** One line, always visible, and the thing you tap to open the sheet. */
  summary: ReactNode;
  /** Also always visible: the sun control, which is the primary interaction. */
  primary: ReactNode;
  /** Revealed on expand. */
  children: ReactNode;
  expanded: boolean;
  onToggle(): void;
}

/** Drag distance, in px, that counts as a deliberate open or close. */
const DRAG_THRESHOLD = 28;

/**
 * The phone layout.
 *
 * On a 390x844 phone the three floating desktop panels covered about two thirds
 * of the screen and left the map -- the entire subject of the app -- as a strip
 * between them. Sideways it was worse: the panels overlapped each other and the
 * "Clear selection" button sat below the bottom edge.
 *
 * So on a phone the panels stop floating and become one sheet. Collapsed it
 * shows the two things worth permanent screen space: what you are looking at,
 * and the sun. Everything else -- the shade breakdown, the appearance toggles
 * -- is one tap away and scrolls.
 *
 * The sheet publishes its own height to `--sheet-h` so the things anchored to
 * the bottom of the map that it does not own (Mapbox's attribution and
 * navigation control, both of which have to stay visible) can sit above it.
 */
export function MobileSheet({ summary, primary, children, expanded, onToggle }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const dragStart = useRef<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const root = document.documentElement;
    // Border box, not `contentRect`: the sheet's padding is its safe-area
    // inset, and measuring the content box left Mapbox's logo tucked under the
    // sheet's bottom edge by exactly that much.
    const observer = new ResizeObserver(([entry]) => {
      const h = entry.borderBoxSize?.[0]?.blockSize ?? el.getBoundingClientRect().height;
      root.style.setProperty('--sheet-h', `${h}px`);
    });
    observer.observe(el);

    return () => {
      observer.disconnect();
      root.style.removeProperty('--sheet-h');
    };
  }, []);

  // A drag on the grip is the gesture people reach for; the tap is what they
  // fall back to. Both are here, and the grip is a real button so the sheet is
  // also operable from a keyboard and announced to a screen reader.
  const onPointerDown = (e: React.PointerEvent) => {
    dragStart.current = e.clientY;
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const start = dragStart.current;
    dragStart.current = null;
    if (start === null) return;

    const dy = e.clientY - start;
    if (Math.abs(dy) < DRAG_THRESHOLD) return; // a tap; the click handler has it
    if (dy < 0 !== expanded) onToggle();
    // Swiping in the direction it is already in is a no-op, not a toggle.
  };

  return (
    <div className="sheet panel" ref={ref}>
      <button
        type="button"
        className="sheet__grip"
        onClick={onToggle}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        aria-expanded={expanded}
        aria-controls="sheet-body"
      >
        <span className="sheet__grip-bar" aria-hidden="true" />
        <span className="sheet__summary">{summary}</span>
        <span className="sheet__chevron" aria-hidden="true" />
        <span className="sr-only">{expanded ? 'Hide details' : 'Show details'}</span>
      </button>

      {primary}

      <div className="sheet__body" id="sheet-body" aria-hidden={!expanded}>
        <div className="sheet__scroll">
          {/* inert while collapsed so nothing inside takes focus off-screen */}
          <div className="sheet__inner" inert={!expanded}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
